import type { AppConfig, AppConfigOverrides, AppCustomization, ChildAppConfig, IconDef, LegacyAppConfig } from './types'
import { getLayoutSnapshot } from './layout-snapshot-getter'
import { getMountRoutes } from './mount-routes-getter'

// ⚠️ MIGRATION REQUIREMENT: When bumping schema version (e.g., 2.0 → 2.1):
// 1. Add new version constant to SUPPORTED_SCHEMAS
// 2. Write migration function (e.g., migrate_2_0_to_2_1)
// 3. Add console.warn for older version detection
// 4. Update CURRENT_SCHEMA
// 5. Update XSD generation
// Failure to do ANY of these will break backward compatibility.

export const CURRENT_SCHEMA = 'wippy-context-2.0'
export const SUPPORTED_SCHEMAS = ['wippy-context-2.0'] as const

/**
 * Build a full schema URL from the schema name and current deployment.
 * Result: 'https://web-host.wippy.ai/webcomponents-1.0.18/schemas/wippy-context-2.0.xsd'
 */
export function buildSchemaUrl(schemaName: string = CURRENT_SCHEMA): string {
  const baseUrl = typeof import.meta !== 'undefined' ? (import.meta.env?.APP_URL || '') : ''
  const tag = typeof import.meta !== 'undefined' ? (import.meta.env?.APP_CURRENT_TAG || '') : ''
  const tagPart = tag ? `${tag}/` : ''
  return `${baseUrl}/${tagPart}schemas/${schemaName}.xsd`
}

/**
 * Extract schema version name from $schema URL.
 * 'https://web-host.wippy.ai/webcomponents-1.0.17/schemas/wippy-context-2.0.xsd' → 'wippy-context-2.0'
 */
export function getSchemaVersion(data: unknown): string | null {
  const schema = (data as any)?.$schema
  if (!schema)
    return null
  const match = String(schema).match(/wippy-context-\d+(?:\.\d+)*/)
  return match?.[0] ?? null
}

export function isLegacyConfig(data: unknown): boolean {
  return getSchemaVersion(data) === null
}

/**
 * Migrate any config format to current AppConfig.
 * Host-side: receives full config from parent website.
 *
 * ⚠️ MIGRATION REQUIREMENT: Add new branches here when bumping schema version
 */
export function migrateConfig(data: unknown): AppConfig {
  const version = getSchemaVersion(data)

  if (version === null) {
    console.warn(
      '[Wippy] Legacy AppConfig format detected (no $schema). This format is deprecated.\n'
      + 'See migration guide in README.md',
    )
    return convertLegacyConfig(data as LegacyAppConfig)
  }

  if (version === CURRENT_SCHEMA)
    return data as AppConfig

  // Older versioned schema — migrate forward
  // ⚠️ Add new version migrations here as elif branches
  console.warn(
    `[Wippy] Older config schema "${version}" detected. Current is "${CURRENT_SCHEMA}".\n`
    + 'Config will be auto-migrated. Please update your configuration.',
  )

  // Example future migration chain:
  // if (version === 'wippy-context-2.0') data = migrate_2_0_to_2_1(data)

  return data as AppConfig
}

/**
 * Migrate config received by child iframe (ChildAppConfig).
 * Proxy-side: receives flat projection from host.
 */
export function migrateChildConfig(data: unknown): ChildAppConfig {
  const version = getSchemaVersion(data)

  if (version === null) {
    // Legacy format — child received old-style flat config
    // Convert to ChildAppConfig shape
    const legacy = data as LegacyAppConfig
    console.warn(
      '[Wippy] Legacy config format detected in child iframe. This format is deprecated.',
    )
    return {
      $schema: buildSchemaUrl(),
      auth: legacy.auth,
      env: legacy.feature?.env ?? { APP_API_URL: '', APP_AUTH_API_URL: '', APP_WEBSOCKET_URL: '' },
      axiosDefaults: legacy.feature?.axiosDefaults,
      routePrefix: legacy.feature?.routePrefix,
      apiRoutes: legacy.feature?.apiRoutes,
      theming: {
        global: legacy.customization ?? {},
      },
      context: {
        resourceId: legacy.artifactId ?? '',
        resourceType: 'page',
        route: legacy.path,
      },
    }
  }

  if (version === CURRENT_SCHEMA)
    return data as ChildAppConfig

  // Future version migrations here
  console.warn(
    `[Wippy] Older child config schema "${version}" detected. Current is "${CURRENT_SCHEMA}".`,
  )
  return data as ChildAppConfig
}

/**
 * Convert legacy (no $schema) config to current AppConfig.
 *
 * ⚠️ MIGRATION REQUIREMENT: When adding a new schema version,
 * do NOT modify this function. Instead, add a new migrate_X_to_Y function
 * and chain it in migrateConfig().
 */
export function convertLegacyConfig(legacy: LegacyAppConfig): AppConfig {
  return {
    $schema: buildSchemaUrl(),

    auth: legacy.auth,
    env: legacy.feature?.env ?? { APP_API_URL: '', APP_AUTH_API_URL: '', APP_WEBSOCKET_URL: '' },
    axiosDefaults: legacy.feature?.axiosDefaults,
    routePrefix: legacy.feature?.routePrefix,
    apiRoutes: legacy.feature?.apiRoutes,
    // themeMode intentionally omitted: legacy configs predate it → defaults to 'auto'.

    theming: {
      global: {
        cssVariables: legacy.customization?.cssVariables,
        customCSS: legacy.customization?.customCSS,
        // Legacy icons → iconSets with 'custom' prefix (don't migrate to deprecated icons field)
        iconSets: legacy.customization?.icons ? { custom: legacy.customization.icons } : undefined,
      },
      host: {
        i18n: legacy.customization?.i18n,
      },
    },

    hostConfig: {
      showAdmin: legacy.feature?.showAdmin,
      startNavOpen: legacy.feature?.startNavOpen,
      hideNavBar: legacy.feature?.hideNavBar,
      disableRightPanel: legacy.feature?.disableRightPanel,
      hideSessionSelector: legacy.feature?.hideSessionSelector,
      allowSelectModel: legacy.feature?.allowSelectModel,
      additionalNavItems: legacy.feature?.additionalNavItems,
      chat: legacy.feature?.chat,
      stateCache: legacy.feature?.stateCache,
      allowAdditionalTags: legacy.feature?.allowAdditionalTags,
      session: legacy.feature?.session,
      history: legacy.feature?.history,
    },

    context: {
      resourceId: legacy.artifactId ?? '',
      resourceType: 'page',
      route: legacy.path,
    },
  }
}

/**
 * Merge theming scopes for host consumption (global + host).
 */
export function getHostTheming(config: AppConfig): AppCustomization {
  const g = config.theming.global ?? {}
  const h = config.theming.host ?? {}
  // Merge deprecated icons into iconSets under 'custom' prefix, then merge iconSets
  const mergedIconSets = { ...g.iconSets, ...h.iconSets }
  if (g.icons || h.icons)
    mergedIconSets.custom = { ...mergedIconSets.custom, ...g.icons, ...h.icons }
  return {
    cssVariables: { ...g.cssVariables, ...h.cssVariables } as AppCustomization['cssVariables'],
    customCSS: [g.customCSS, h.customCSS].filter(Boolean).join('\n') || undefined,
    iconSets: Object.keys(mergedIconSets).length ? mergedIconSets : undefined,
    i18n: h.i18n,
  }
}

/**
 * Merge theming scopes for child consumption (global + children).
 */
export function getChildTheming(config: AppConfig): AppCustomization {
  const g = config.theming.global ?? {}
  const c = config.theming.children ?? {}
  const mergedIconSets = { ...g.iconSets }
  if (g.icons)
    mergedIconSets.custom = { ...mergedIconSets.custom, ...g.icons }
  return {
    cssVariables: { ...g.cssVariables, ...c.cssVariables } as AppCustomization['cssVariables'],
    customCSS: [g.customCSS, c.customCSS].filter(Boolean).join('\n') || undefined,
    iconSets: Object.keys(mergedIconSets).length ? mergedIconSets : undefined,
  }
}

/**
 * Apply a per-page `customization` overlay onto a base `AppCustomization`.
 * Single source of truth for the host-side `buildChildPayload` merge AND
 * the dev-proxy `applyPackageCustomization` reader — keeping both paths
 * in lock-step so dev preview matches host behavior.
 *
 *   - `cssVariables` / `customCSS`: REPLACE (overlay supplies its own theme)
 *   - `icons` / `iconSets`: MERGE (additive)
 *
 * Mutates and returns `base`. Pass a freshly-cloned copy if the caller
 * doesn't want mutation.
 */
export function mergeChildCustomization(
  base: AppCustomization,
  overlay: AppCustomization | undefined,
): AppCustomization {
  if (!overlay)
    return base
  if (overlay.cssVariables)
    base.cssVariables = overlay.cssVariables
  if (overlay.customCSS != null)
    base.customCSS = overlay.customCSS
  if (overlay.icons)
    base.icons = { ...base.icons, ...overlay.icons }
  if (overlay.iconSets) {
    const baseSets = base.iconSets ?? {}
    const mergedSets: Record<string, Record<string, IconDef>> = {}
    for (const [prefix, icons] of Object.entries({ ...baseSets, ...overlay.iconSets }))
      mergedSets[prefix] = { ...baseSets[prefix], ...icons }
    base.iconSets = mergedSets
  }
  return base
}

/**
 * Build the SetConfig payload for a child iframe.
 * Merges theming, strips hostConfig/theming tiers, and injects the current
 * host mountRoutes map so child link classifiers can detect host-nav links.
 */
export function buildChildPayload(
  config: AppConfig,
  context: Partial<AppConfig['context']>,
  configOverrides?: AppConfigOverrides,
): ChildAppConfig {
  const mountRoutes = getMountRoutes()

  // Merge theming for child: parent's global + children → child's global.
  const childThemingGlobal: AppCustomization = getChildTheming(config)

  // Apply per-page configOverrides host-side (children receive merged config)
  mergeChildCustomization(childThemingGlobal, configOverrides?.customization)

  return {
    $schema: config.$schema,
    auth: config.auth,
    env: config.env,
    axiosDefaults: configOverrides?.axiosDefaults
      ? { ...config.axiosDefaults, ...configOverrides.axiosDefaults }
      : config.axiosDefaults,
    routePrefix: configOverrides?.routePrefix ?? config.routePrefix,
    apiRoutes: configOverrides?.apiRoutes ?? config.apiRoutes,
    tanstack: config.tanstack,
    themeMode: configOverrides?.themeMode ?? config.themeMode,
    theming: { global: childThemingGlobal },
    context: {
      resourceId: context.resourceId ?? '',
      resourceType: context.resourceType ?? 'page',
      route: context.route,
      parentResourceId: context.parentResourceId,
      nestingDepth: context.nestingDepth,
      isNavOwner: context.isNavOwner,
      layoutPanelId: context.layoutPanelId,
      layoutId: context.layoutId,
      // Seed the child's host.layout.snapshot with the host's current
      // snapshot. Callers can override via context.layout if they have a
      // fresher value; otherwise this pulls from the installed getter
      // (null outside a managed-layout host).
      layout: context.layout ?? getLayoutSnapshot() ?? undefined,
      extensions: context.extensions,
    },
    selfPageId: context.resourceId || undefined,
    ...(mountRoutes && Object.keys(mountRoutes).length > 0 ? { mountRoutes } : {}),
  }
}
